import { useState } from 'react';
import { ChevronUp, ChevronDown, Palette, Settings2, LayoutList, Eye, PencilRuler } from 'lucide-react';
import { useSiteQuery, useSaveTheme } from '../features/site/hooks.ts';
import { siteNotProvisioned } from '../features/site/api.ts';
import { buildMenuRows, moveInOrder, withMenu, type MenuRow } from '../features/site/menu.ts';
import { DesignPanel } from '../features/site/DesignPanel.tsx';
import { SettingsPanel } from '../features/site/SettingsPanel.tsx';
import { MenuSectionPanel } from '../features/site/MenuSectionPanel.tsx';
import { PreviewPane } from '../features/site/PreviewPane.tsx';
import { SiteTopBar } from '../features/site/SiteTopBar.tsx';
import type { SiteBundle } from '../features/site/api.ts';

type Sel = { kind: 'section'; id: string } | { kind: 'design' } | { kind: 'settings' };

function RailButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-start text-sm font-medium transition-colors ${active ? 'bg-primary/10 text-primary' : 'text-foreground/80 hover:bg-muted'}`}
    >
      {children}
    </button>
  );
}

function BuilderInner({ bundle }: { bundle: SiteBundle }): JSX.Element {
  const saveTheme = useSaveTheme();
  const rows = buildMenuRows(bundle);
  const [sel, setSel] = useState<Sel>(() => (rows[0] ? { kind: 'section', id: rows[0].id } : { kind: 'design' }));
  const [mobile, setMobile] = useState<'edit' | 'preview'>('edit');

  const theme = bundle.site.theme ?? {};
  const reorder = (id: string, dir: -1 | 1): void => {
    const menuOrder = moveInOrder(rows, id, dir);
    saveTheme.mutate({ theme: withMenu(theme, { menuOrder }) });
  };

  const selectedRow: MenuRow | undefined = sel.kind === 'section' ? rows.find((r) => r.id === sel.id) : undefined;
  const previewPath = sel.kind === 'section' ? sel.id : '';

  return (
    <div className="flex h-[calc(100vh-var(--topbar,49px))] min-h-0">
      {/* Left rail */}
      <aside className={`w-60 shrink-0 overflow-y-auto border-e border-border p-2 ${mobile === 'preview' ? 'hidden xl:block' : ''}`}>
        <p className="px-2 pb-1.5 pt-1 text-[0.7rem] font-semibold uppercase tracking-wider text-muted-foreground">Menu &amp; pages</p>
        <ul className="space-y-0.5">
          {rows.map((r, i) => (
            <li key={r.id} className="group flex items-center gap-1">
              <RailButton active={sel.kind === 'section' && sel.id === r.id} onClick={() => setSel({ kind: 'section', id: r.id })}>
                <span className="truncate">{r.labelEn}</span>
                {!r.mandated && <span className="pill pill-neutral ms-auto shrink-0 text-[0.6rem]">optional</span>}
              </RailButton>
              <div className="flex flex-col opacity-0 transition-opacity group-hover:opacity-100">
                <button type="button" aria-label={`Move ${r.labelEn} up`} disabled={i === 0} onClick={() => reorder(r.id, -1)} className="text-muted-foreground hover:text-foreground disabled:opacity-20"><ChevronUp size={14} /></button>
                <button type="button" aria-label={`Move ${r.labelEn} down`} disabled={i === rows.length - 1} onClick={() => reorder(r.id, 1)} className="text-muted-foreground hover:text-foreground disabled:opacity-20"><ChevronDown size={14} /></button>
              </div>
            </li>
          ))}
        </ul>

        <div className="my-2 border-t border-border" />
        <p className="px-2 pb-1.5 pt-1 text-[0.7rem] font-semibold uppercase tracking-wider text-muted-foreground">Site</p>
        <div className="space-y-0.5">
          <RailButton active={sel.kind === 'design'} onClick={() => setSel({ kind: 'design' })}><Palette size={15} /> Design &amp; branding</RailButton>
          <RailButton active={sel.kind === 'settings'} onClick={() => setSel({ kind: 'settings' })}><Settings2 size={15} /> Settings</RailButton>
        </div>
      </aside>

      {/* Middle controls */}
      <div className={`min-w-0 flex-1 overflow-y-auto p-5 xl:max-w-xl ${mobile === 'preview' ? 'hidden xl:block' : ''}`}>
        {selectedRow && <MenuSectionPanel key={selectedRow.id} bundle={bundle} row={selectedRow} />}
        {sel.kind === 'design' && <DesignPanel key="design" site={bundle.site} />}
        {sel.kind === 'settings' && <SettingsPanel key="settings" site={bundle.site} optional={bundle.optional} />}
      </div>

      {/* Preview */}
      <div className={`min-w-0 flex-1 border-s border-border ${mobile === 'edit' ? 'hidden xl:block' : ''}`}>
        <PreviewPane path={previewPath} />
      </div>

      {/* Mobile edit/preview toggle */}
      <div className="fixed bottom-4 left-1/2 z-30 -translate-x-1/2 xl:hidden">
        <div className="inline-flex rounded-full border border-border bg-paper p-1 shadow-lg">
          <button type="button" onClick={() => setMobile('edit')} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium ${mobile === 'edit' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`}><PencilRuler size={14} /> Edit</button>
          <button type="button" onClick={() => setMobile('preview')} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium ${mobile === 'preview' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`}><Eye size={14} /> Preview</button>
        </div>
      </div>
    </div>
  );
}

export function Builder(): JSX.Element {
  const { data: bundle, isLoading, error } = useSiteQuery();

  if (isLoading) return <div className="page"><p role="status" className="status-label">Loading…</p></div>;

  if (error && siteNotProvisioned(error)) {
    return (
      <div className="page">
        <div className="card-paper mx-auto mt-10 max-w-lg space-y-3 p-8 text-center">
          <LayoutList className="mx-auto text-primary" />
          <h1 className="font-serif text-2xl text-ink">Your council site hasn't been set up yet</h1>
          <p className="text-sm text-muted-foreground">A WapSite hasn't been provisioned for your council yet. Once it is, you'll design and fill it in here.</p>
        </div>
      </div>
    );
  }
  if (error || !bundle) {
    return (
      <div className="page">
        <div role="alert" className="max-w-lg rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          Something went wrong loading your site. Please refresh and try again.
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <SiteTopBar bundle={bundle} tool="builder" />
      <BuilderInner bundle={bundle} />
    </div>
  );
}
